import React, { useContext, useState } from "react";
import "./team-tab-content.component.scoped.scss";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { CaseContext } from "../../../../../store";
import { TableColumn } from "../../../../../types/table-column";
import { formatDate } from "../../../../../helpers";
import { AddUserDialog } from "../../../../../dialogs/add-user/add-user";
import { Skeleton } from "@material-ui/lab";
import { SorterComponent } from "../../../../../components/table/sorter/sorter.component";
import { teamColumns } from "../../../../../statics/table-columns/team-columns";
import TabContentTitleComponent from "../tab-content-title/tab-content-title.component";
import { UserRoles } from "../../../../../types/userRoles";

const TeamTabContentComponent = (props: any) => {
  const { sortUsers } = props;
  const { t } = useTranslation();
  const { caseUsers, usersLoading, caseDetails } = useContext(CaseContext);
  const [userImageError, setUserImageError] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [tableOption, setTableOption] = useState({
    sortDirection: "asc",
    sortColumn: "",
  });

  const sort = (sortColumn: string, sortDirection: string) => {
    const sortOption = {
      order: sortDirection,
      sort: sortColumn,
    };
    setTableOption({
      sortColumn,
      sortDirection,
    });
    sortUsers(caseDetails.id, sortOption);
  };

  return (
    <Box className="team__content">
      <TabContentTitleComponent
        title={t("USECASE_DETAILS.ASSIGNED_TEAM")}
        subTitle={t("USECASE_DETAILS.ASSIGNED_TEAM_SUB_TITLE")}
        loading={usersLoading}
      />
      <Box className="global__box__content p-0">
        <Box className="global__box__content__header">
          {usersLoading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Box className="d-flex align-items-center" style={{ gap: ".5rem" }}>
              <Typography
                className="text-capitalize"
                variant="h3"
                color="textPrimary"
                style={{
                  fontWeight: 500,
                }}
              >
                {t("USECASE_DETAILS.ASSIGNED_TEAM")}
              </Typography>

              <Typography variant="caption">
                {`${caseUsers.length} ${t("USER")} 
            (${
              caseUsers.filter((item: any) => item.role === UserRoles.EDITOR)
                .length
            } ${t("ROLES.EDITOR")} / ${
                  caseUsers.filter(
                    (item: any) => item.role === UserRoles.AUDITOR,
                  ).length
                } ${t("ROLES.AUDITOR")} / ${
                  caseUsers.filter(
                    (item: any) => item.role === UserRoles.CONTRIBUTOR,
                  ).length
                } ${t("ROLES.CONTRIBUTOR")}
            )`}
              </Typography>
            </Box>
          )}

          {usersLoading ? (
            <Skeleton variant="rect" height={35} width={220} />
          ) : (
            <Button
              className="btn-primary-theme"
              variant="contained"
              size="small"
              color="primary"
              onClick={setShowUserModal.bind(null, true)}
            >
              {t("USERS_MANAGEMENT")}
            </Button>
          )}
        </Box>
        <Box className="global__box__content__body p-0">
          <table>
            <thead>
              <tr>
                {teamColumns.map((column: TableColumn, index: number) => {
                  return (
                    <th key={index} style={{ width: column.width }}>
                      <Box
                        className="d-flex align-items-center"
                        style={{ gap: ".5rem" }}
                      >
                        {t(column.fieldName)}

                        {column.type !== "keyContact" && (
                          <SorterComponent
                            isSorting={
                              column.fieldName === tableOption.sortColumn
                            }
                            direction={tableOption.sortDirection}
                            sortColumn={tableOption.sortColumn}
                            fieldName={column.value}
                            sort={sort}
                          />
                        )}
                      </Box>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {usersLoading && caseUsers && !caseUsers.length && (
                <tr className="loader-area">
                  <td colSpan={teamColumns.length} className="text-center">
                    <CircularProgress />
                  </td>
                </tr>
              )}
              {caseUsers && caseUsers.length === 0 && !usersLoading && (
                <tr>
                  <td colSpan={teamColumns.length} className="text-center py-5">
                    {t("NO_DATA_AVAILABLE")}
                  </td>
                </tr>
              )}
              {caseUsers.map((row: any, rowIndex: number) => {
                return (
                  <tr key={rowIndex}>
                    {teamColumns.map(
                      (column: TableColumn, columnIndex: number) => {
                        const { type, value, secondaryValue }: any = column;
                        return (
                          <td key={columnIndex} style={{ width: column.width }}>
                            {column.type === "string" ? (
                              <Box
                                className={
                                  secondaryValue ? "double-column" : ""
                                }
                              >
                                <Typography variant="subtitle1">
                                  {row[column.value]}
                                </Typography>
                                {secondaryValue && (
                                  <Box>
                                    {
                                      <Typography
                                        variant="body1"
                                        className="hint"
                                      >
                                        {row[secondaryValue]}
                                      </Typography>
                                    }
                                  </Box>
                                )}
                              </Box>
                            ) : column.type === "client" ? (
                              <Box
                                className={`d-flex align-items-center ${
                                  secondaryValue ? " double-column" : ""
                                }`}
                                style={{ gap: ".5rem" }}
                              >
                                <Box className="user__profile__pic">
                                  {row.profilePicURL && !userImageError ? (
                                    <img
                                      src={row.profilePicURL}
                                      alt=""
                                      onError={setUserImageError.bind(
                                        null,
                                        true,
                                      )}
                                    />
                                  ) : (
                                    <span className="text-uppercase">
                                      {row.firstName[0] + row.lastName[0]}
                                    </span>
                                  )}
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1">
                                    {row.firstName + " " + row.lastName}
                                  </Typography>
                                  <Typography variant="body1">
                                    {row[secondaryValue]}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : column.type === "role" ? (
                              <Box
                                className={
                                  secondaryValue ? "double-column" : ""
                                }
                              >
                                <Typography variant="subtitle1">
                                  {t(`ROLES.${row[column.value]}`)}
                                </Typography>
                                {secondaryValue && (
                                  <Typography variant="body1" className="hint">
                                    {t(row[secondaryValue])}
                                  </Typography>
                                )}
                              </Box>
                            ) : column.type === "date" ? (
                              <Typography variant="subtitle1">
                                {formatDate(row[column.value], "DD.MM.YYYY")}
                              </Typography>
                            ) : type === "keyContact" ? (
                              <Typography variant="body1">
                                {+row.id === +caseDetails.userId
                                  ? t("YES")
                                  : t("NO")}
                              </Typography>
                            ) : (
                              <Box className={type}>{row[value]}</Box>
                            )}
                          </td>
                        );
                      },
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>
      {showUserModal && (
        <AddUserDialog
          show={showUserModal}
          onClose={() => setShowUserModal(false)}
          isEditable={true}
        />
      )}
    </Box>
  );
};

export default TeamTabContentComponent;
